import { z } from "zod";
export const MAPPING_ENTITY_TYPES = ["PROJECT", "TIME_ENTRY", "PERSON", "TOPIC", "BUSINESS_MANAGER_ASSIGNMENT", "WORK_PACKAGE", "INVOICE"];
/**
 * Interne Zielfelder für den Auftrags-/Projektimport (Aufgabenstellung Abschnitt 4.1).
 * An die reale SharePoint-Auftragsliste angepasst (siehe scripts/sharepoint-export.js
 * DEFAULT_FIELDS und docs/data-model.md).
 */
export const PROJECT_TARGET_FIELDS = [
    { key: "sharepointId", label: "SharePoint-ID", required: true },
    { key: "projectNumber", label: "Projektnummer (Auftragnummer)", required: true },
    { key: "projectName", label: "Projektname (Titel)", required: true },
    { key: "description", label: "Projektbeschreibung", required: false },
    { key: "customerName", label: "Kunde", required: true },
    { key: "customerNumber", label: "Kundennummer", required: false },
    { key: "customerUniqueId", label: "CustomerUniqueId", required: false },
    { key: "client", label: "Auftraggeber", required: false },
    { key: "endCustomerName", label: "Endkunde", required: false },
    { key: "responsiblePerson", label: "Verantwortliche Person", required: false },
    { key: "projectLead", label: "Projektleitung (Name)", required: false },
    { key: "projectLeadEmail", label: "Projektleitung (E-Mail)", required: false },
    { key: "salesRepNames", label: "Vertrieb (Namen)", required: false },
    { key: "salesRepEmails", label: "Vertrieb (E-Mails)", required: false },
    { key: "caseWorkerName", label: "Sachbearbeitung (Name)", required: false },
    { key: "caseWorkerEmail", label: "Sachbearbeitung (E-Mail)", required: false },
    { key: "status", label: "Status", required: true },
    { key: "projectType", label: "Art", required: false },
    { key: "billingType", label: "Abrechnungsart", required: false },
    { key: "activityBillingType", label: "Tätigkeitsabrechnung", required: false },
    { key: "division", label: "Bereich", required: false },
    { key: "businessUnit", label: "Geschäftsbereich", required: false },
    { key: "internal", label: "Intern", required: false },
    { key: "orderDate", label: "Auftragsdatum", required: false },
    { key: "startDate", label: "Startdatum", required: false },
    { key: "endDate", label: "Enddatum", required: false },
    { key: "contractEndDate", label: "Laufzeitende", required: false },
    { key: "fiscalYear", label: "Geschäftsjahr", required: false },
    { key: "orderVolume", label: "Auftragsvolumen", required: false },
    { key: "orderIntakeSum", label: "Summe Auftragseingang", required: false },
    { key: "revenue", label: "Umsatz", required: false },
    { key: "monthlyRetainerNet", label: "Monatliche Bereitstellungspauschale (Netto)", required: false },
    { key: "plannedDays", label: "Geplante Personentage (Anzahl PT)", required: false },
    { key: "actualDays", label: "Abgeleistete Personentage (AufwandTT)", required: false },
    { key: "calculatedDaysTT", label: "Kalkulierte Personentage (KalkuliertTT)", required: false },
    { key: "remainingDays", label: "Restpersonentage", required: false },
    { key: "timeTrackerId", label: "TimeTracker-Projekt-GUID", required: false },
    { key: "timeTrackerStatus", label: "Status (TimeTracker)", required: false },
    { key: "lastBookingAt", label: "Letzte Buchung (TimeTracker)", required: false },
    { key: "lastInvoiceAt", label: "Letzte Rechnung", required: false },
    { key: "ttDataAsOf", label: "Stand TimeTracker-Daten", required: false },
    { key: "plannedTeamSize", label: "Anzahl Projektmitarbeiter (geplant)", required: false },
    { key: "actualTeamSize", label: "Anzahl Projektmitarbeiter (Ist)", required: false },
    { key: "orgUnit", label: "Organisationseinheit", required: false },
    { key: "location", label: "Standort", required: false },
    { key: "projectRoomUrl", label: "Projektraumlink", required: false },
    { key: "jiraLink", label: "Jira-Link", required: false },
    { key: "crmOpportunityId", label: "CRM-Opportunity-ID", required: false },
    { key: "projectReferenceName", label: "Projektreferenz", required: false },
    { key: "customerContactName", label: "Ansprechpartner Kunde", required: false },
    { key: "customerContactEmail", label: "Ansprechpartner Kunde (E-Mail)", required: false },
    { key: "customerContactSalutation", label: "Ansprechpartner Anrede", required: false },
    { key: "projectStartStatus", label: "Projektstart-Status", required: false },
    { key: "projectStartComment", label: "Projektstart-Kommentar", required: false },
    { key: "tags", label: "Tags", required: false },
    { key: "notes", label: "Notizen", required: false },
    { key: "comment", label: "Kommentar", required: false },
    { key: "sourceCreatedAt", label: "Erstellt (SharePoint)", required: false },
    { key: "sourceModifiedAt", label: "Geändert (SharePoint)", required: false },
];
/** Interne Zielfelder für den TimeTracker-Tätigkeitsimport (Aufgabenstellung Abschnitt 4.2). */
export const TIME_ENTRY_TARGET_FIELDS = [
    { key: "timeTrackerProjectId", label: "TimeTracker-Projekt-ID", required: true },
    { key: "projectNumber", label: "Projektnummer", required: false },
    { key: "projectNameRaw", label: "Projektname", required: false },
    { key: "workPackage", label: "Arbeitspaket", required: false },
    // Echte GUID des Arbeitspakets (PM_Arbeitspakete_UID), identischer Wertebereich wie
    // WorkPackage.externalId - erlaubt eine zuverlässige Verknüpfung zum WorkPackage-Datensatz,
    // siehe schema.prisma (TimeEntry.workPackageExternalId).
    { key: "workPackageExternalId", label: "Arbeitspaket-ID (ArbeitspaketGUID)", required: false },
    // Bewusst nur IDs, keine Namen: die Zuordnung zu Anzeigenamen erfolgt über bereits im
    // OrderAnalyzer vorhandene Stammdaten (Personen-/Projektimport), nicht über den TimeTracker-
    // Export selbst (Datenschutz, für die KI-Themenanalyse ohnehin unerheblich). "employeeName"
    // bleibt als optionales Fallback-Feld für Quellen ohne ID nutzbar.
    { key: "employeeId", label: "Mitarbeiter-ID", required: false },
    { key: "employeeName", label: "Mitarbeitername", required: false },
    { key: "customerId", label: "Kunden-ID", required: false },
    { key: "orgUnit", label: "Organisationseinheit", required: false },
    { key: "role", label: "Rolle", required: false },
    { key: "activityDate", label: "Datum", required: true },
    { key: "startTime", label: "Uhrzeit Beginn", required: false },
    { key: "endTime", label: "Uhrzeit Ende", required: false },
    { key: "breakMinutes", label: "Pause (Minuten)", required: false },
    { key: "hours", label: "Dauer in Stunden", required: false },
    { key: "days", label: "Dauer in Personentagen", required: false },
    { key: "description", label: "Beschreibung", required: false },
    { key: "comment", label: "Kommentar", required: false },
    { key: "activityType", label: "Tätigkeitstyp", required: false },
    { key: "bookingCategory", label: "Buchungskategorie", required: false },
    { key: "billable", label: "Abrechenbarkeit", required: false },
];
/** Interne Zielfelder für den Personenimport (Aufgabenstellung Abschnitt 4.3). */
export const PERSON_TARGET_FIELDS = [
    { key: "externalEmployeeId", label: "Personen-ID", required: false },
    { key: "name", label: "Name", required: true },
    { key: "email", label: "E-Mail", required: false },
    { key: "orgUnit", label: "Organisationseinheit", required: false },
    { key: "role", label: "Rolle", required: false },
    { key: "seniority", label: "Senioritätsstufe", required: false },
    { key: "employmentStatus", label: "Beschäftigungsstatus", required: false },
    { key: "company", label: "Firma", required: false },
    { key: "availableCapacityDays", label: "Verfügbare Kapazität (Personentage)", required: false },
    { key: "weeklyCapacityDays", label: "Wöchentliche Kapazität (Personentage)", required: false },
    { key: "includeInPlanning2027", label: "Für Planung 2027 berücksichtigen", required: false },
];
/**
 * Interne Zielfelder für den Themenimport. Die meisten Felder dienen ausschließlich dazu, die
 * Trefferquote der KI-Themenzuordnung zu erhöhen (siehe docs/ai-prompt.md) - sie fließen 1:1 in
 * den KI-Prompt ein (prompts.ts).
 */
export const TOPIC_TARGET_FIELDS = [
    { key: "id", label: "Themen-ID", required: false },
    { key: "name", label: "Name", required: true },
    { key: "parentName", label: "Übergeordnetes Thema", required: false },
    { key: "description", label: "Beschreibung", required: false },
    { key: "synonyms", label: "Synonyme", required: false },
    { key: "typicalTechnologies", label: "Typische Technologien", required: false },
    { key: "typicalActivities", label: "Typische Tätigkeiten", required: false },
    { key: "keyTerms", label: "Schlüsselbegriffe", required: false },
    { key: "positiveIndicators", label: "Positive Indikatoren", required: false },
    { key: "exclusionCriteria", label: "Negative Indikatoren", required: false },
    { key: "similarTopicsDelineation", label: "Abgrenzung zu ähnlichen Themen", required: false },
    { key: "keywordWeight", label: "Gewichtung", required: false },
    { key: "examples", label: "Beispiele", required: false },
    { key: "aiExamplePrompts", label: "KI-Beispielprompts", required: false },
    { key: "active", label: "Aktiv", required: false },
    { key: "assignableToProjects", label: "Für Zuordnung zu Projekten nutzbar", required: false },
];
/**
 * Interne Zielfelder für den Import der Business-Manager-Zuordnung (Auftragsnummer -> Business
 * Manager, prozentuale Aufteilung). Eine Auftragsnummer kann mehrfach vorkommen (eine Zeile je
 * Business Manager), z.B. bei einem 50/50-Split auf zwei Business Manager - siehe
 * importService.ts#startBusinessManagerAssignmentImport.
 */
export const BUSINESS_MANAGER_ASSIGNMENT_TARGET_FIELDS = [
    { key: "projectNumber", label: "Auftragsnummer", required: true },
    { key: "businessManagerName", label: "Business Manager (Anzeigename)", required: true },
    { key: "percentage", label: "Prozentuale Beteiligung BM", required: true },
];
/**
 * Interne Zielfelder für den Arbeitspaket-Import (dbo.vwAdmin_WorkPackages, siehe
 * docs/timetracker-reference.md). Ein Arbeitspaket wird über projectExternalId (= Project.timeTrackerId)
 * einem Projekt zugeordnet - analog zu timeTrackerProjectId beim Tätigkeitsimport.
 */
export const WORK_PACKAGE_TARGET_FIELDS = [
    { key: "externalId", label: "Arbeitspaket-ID (UniqueId)", required: true },
    { key: "projectExternalId", label: "TimeTracker-Projekt-GUID (ProjectUniqueId)", required: true },
    { key: "subProject", label: "Teilprojekt", required: false },
    { key: "service", label: "Leistung", required: false },
    { key: "number", label: "Nummer", required: false },
    { key: "name", label: "Name", required: false },
    { key: "description", label: "Beschreibung", required: false },
    { key: "effortMinimal", label: "Aufwand minimal (PT)", required: false },
    { key: "effortMedium", label: "Aufwand mittel (PT)", required: false },
    { key: "effortMaximum", label: "Aufwand maximal (PT)", required: false },
    { key: "effortCalculated", label: "Kalkulierter Aufwand (PT)", required: false },
    { key: "dayRate", label: "Tagessatz", required: false },
    { key: "companyShare", label: "Unternehmensanteil (%)", required: false },
    { key: "companyEffort", label: "Unternehmensaufwand (PT)", required: false },
    { key: "customerShare", label: "Kundenanteil (%)", required: false },
    { key: "customerEffort", label: "Kundenaufwand (PT)", required: false },
    { key: "dateFrom", label: "Von", required: false },
    { key: "dateUntil", label: "Bis", required: false },
    { key: "divisionId", label: "Division (GUID)", required: false },
    { key: "divisionName", label: "Division (Name)", required: false },
    { key: "effortInvoiced", label: "Aufwand fakturiert (PT)", required: false },
    { key: "effortNotInvoiced", label: "Aufwand nicht fakturiert (PT)", required: false },
    { key: "effortAll", label: "Aufwand gesamt gebucht (PT)", required: false },
    { key: "isDeletable", label: "Löschbar", required: false },
    { key: "isLocked", label: "Gesperrt", required: false },
    { key: "isMovable", label: "Verschiebbar", required: false },
];
/**
 * Rechnungen aus dem TimeTracker (dbo.spAdmin_GetInvoiceByProjectUid) - für den Umsatzabgleich
 * gegen den SharePoint-gepflegten Auftragseingang (Project.orderIntakeSum, siehe
 * analyticsService.ts). externalId (= PM_OrgaAbrechnung_UID) ist ein echter stabiler Natural Key,
 * analog zu WorkPackage.externalId.
 */
export const INVOICE_TARGET_FIELDS = [
    { key: "externalId", label: "Rechnungs-ID (InvoiceId)", required: true },
    { key: "projectExternalId", label: "TimeTracker-Projekt-GUID (ProjectUniqueId)", required: true },
    { key: "invoiceNumber", label: "Rechnungsnummer", required: false },
    { key: "invoiceDate", label: "Rechnungsdatum", required: false },
    { key: "serviceCostsAmount", label: "Leistungsbetrag", required: false },
    { key: "extraCostsAmount", label: "Nebenkostenbetrag", required: false },
];
export const fieldMappingSchema = z.record(z.string(), z.string().nullable());
export const IMPORT_MODES = ["ADD", "UPDATE", "REPLACE", "DRY_RUN"];
